import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { Pillar } from '@/types'

import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { accentClasses } from '@/constants/hubs'
import { pillarIcon } from '@/constants/pillars'

interface PillarCardProps {
    pillar: Pillar
    className?: string
}

export const PillarCard: FC<PillarCardProps> = ({ pillar, className }): ReactNode => {
    const Icon = pillarIcon(pillar.slug)
    const colors = accentClasses(pillar.accent)

    return (
        <Link
            href={`/${pillar.slug}`}
            className={cn(
                'group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-primary/40',
                className
            )}
        >
            <div className='relative aspect-[16/10] overflow-hidden bg-muted'>
                {pillar.coverImage && (
                    <img
                        src={pillar.coverImage}
                        alt=''
                        loading='lazy'
                        className='absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                    />
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <div className='flex items-center gap-3'>
                    <span
                        className={cn(
                            'flex size-9 shrink-0 items-center justify-center rounded-full',
                            colors.bgSoft,
                            colors.text
                        )}
                    >
                        <Icon className='size-5' />
                    </span>
                    <h3 className='font-heading text-xl font-semibold tracking-tight text-foreground'>
                        {pillar.name}
                    </h3>
                </div>

                <p className='mt-4 flex-1 text-sm leading-relaxed text-muted-foreground'>
                    {pillar.tagline}
                </p>

                <span
                    className={cn(
                        'mt-5 inline-flex items-center gap-1.5 text-sm font-medium',
                        colors.text
                    )}
                >
                    Read the guide
                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' />
                </span>
            </div>
        </Link>
    )
}
